import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { crmAPI, API_BASE_URL } from '../services/api';

const CRMLeadRespondPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const fetchLeadDetails = async () => {
      if (!id || !token) {
        setError('Missing request credentials.');
        setLoading(false);
        return;
      }
      try {
        const res = await crmAPI.getPublicLead(id, token);
        setData(res.data.data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load request details.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeadDetails();
  }, [id, token]);

  const handleConfirm = () => {
    window.location.href = `${API_BASE_URL}/public/crm/leads/${id}/confirm?token=${token}`;
  };

  const handleDecline = () => {
    window.location.href = `${API_BASE_URL}/public/crm/leads/${id}/decline?token=${token}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex justify-center items-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col justify-center items-center p-4">
        <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl shadow-xl max-w-md w-full text-center border border-slate-100 dark:border-slate-800">
          <XCircleIcon className="h-16 w-16 text-rose-500 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Request Error</h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">{error || 'Unable to access this collaboration request.'}</p>
          <a href="/" className="inline-flex justify-center py-3 px-6 rounded-2xl bg-slate-900 hover:bg-slate-850 text-white font-bold text-sm transition-all w-full">
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }

  const { lead, creator } = data;
  const isPending = lead.status === 'pending_confirmation';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col justify-center py-12 sm:px-6 lg:px-8 px-4">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
          Collaboration Proposal
        </h2>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          From {creator?.first_name} {creator?.last_name}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-900 py-8 px-4 shadow-xl rounded-3xl sm:px-10 border border-slate-100 dark:border-slate-800 space-y-6"
        >
          {/* Creator details */}
          <div className="flex items-center space-x-4 p-4 bg-slate-50 dark:bg-slate-800/40 rounded-2xl border border-slate-100 dark:border-slate-800/60">
            <div className="w-12 h-12 rounded-2xl bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center font-bold text-xl text-teal-600 dark:text-teal-400 flex-shrink-0">
              {creator?.first_name?.charAt(0) || 'C'}
            </div>
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white">{creator?.first_name} {creator?.last_name}</h3>
              <p className="text-xs text-slate-400">{creator?.email}</p>
            </div>
          </div>

          {/* Status Alert if not pending */}
          {!isPending && (
            <div className={`p-4 rounded-2xl border text-center text-sm font-bold flex items-center justify-center space-x-2 ${
              lead.status === 'confirmed' || lead.status === 'accepted'
                ? 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-100 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-450'
                : 'bg-rose-50 dark:bg-rose-950/20 border-rose-100 dark:border-rose-900/50 text-rose-700 dark:text-rose-450'
            }`}>
              {lead.status === 'confirmed' || lead.status === 'accepted' ? (
                <>
                  <CheckCircleIcon className="w-5 h-5 flex-shrink-0 text-emerald-600" />
                  <span>Proposal has already been Accepted / Confirmed!</span>
                </>
              ) : (
                <>
                  <XCircleIcon className="w-5 h-5 flex-shrink-0 text-rose-600" />
                  <span>Proposal has already been Declined.</span>
                </>
              )}
            </div>
          )}

          {/* Lead Proposal details */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold text-slate-450 dark:text-slate-500 uppercase tracking-widest">Proposal Details</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-50/50 dark:bg-slate-800/20 rounded-xl border border-slate-100/50 dark:border-slate-800/40">
                <span className="text-[10px] text-slate-400 block font-medium">Interest Category</span>
                <span className="text-sm font-bold text-slate-900 dark:text-white capitalize">{lead.interest_type || 'General Shoot'}</span>
              </div>
              {lead.estimated_budget > 0 && (
                <div className="p-3 bg-slate-50/50 dark:bg-slate-800/20 rounded-xl border border-slate-100/50 dark:border-slate-800/40">
                  <span className="text-[10px] text-slate-400 block font-medium">Estimated Budget</span>
                  <span className="text-sm font-bold text-teal-600 dark:text-teal-400">₹{lead.estimated_budget.toLocaleString('en-IN')}</span>
                </div>
              )}
            </div>

            {lead.expected_shoot_at && (
              <div className="p-4 bg-slate-50/50 dark:bg-slate-800/20 rounded-xl border border-slate-100/50 dark:border-slate-800/40">
                <span className="text-[10px] text-slate-400 block font-medium">Expected Date</span>
                <span className="text-sm font-bold text-slate-900 dark:text-white">
                  {new Date(lead.expected_shoot_at).toLocaleDateString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
              </div>
            )}

            {lead.notes && (
              <div className="p-4 bg-slate-50/50 dark:bg-slate-800/20 rounded-xl border border-slate-100/50 dark:border-slate-800/40">
                <span className="text-[10px] text-slate-400 block font-medium mb-1">Message from Creator</span>
                <p className="text-xs text-slate-650 dark:text-slate-350 whitespace-pre-line leading-relaxed font-medium">
                  {lead.notes}
                </p>
              </div>
            )}
          </div>

          {/* Respond Actions */}
          {isPending ? (
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                onClick={handleDecline}
                className="flex-1 py-3.5 px-4 rounded-2xl bg-rose-50 dark:bg-rose-950/20 hover:bg-rose-100 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-455 font-bold text-sm transition-transform active:scale-95 flex items-center justify-center space-x-2 border border-rose-100 dark:border-rose-900/50 cursor-pointer"
              >
                <XCircleIcon className="w-5 h-5" />
                <span>Decline Proposal</span>
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 py-3.5 px-4 rounded-2xl bg-teal-600 hover:bg-teal-700 text-white font-bold text-sm shadow-lg shadow-teal-600/10 transition-transform active:scale-95 flex items-center justify-center space-x-2 cursor-pointer"
              >
                <CheckCircleIcon className="w-5 h-5" />
                <span>Accept Proposal</span>
              </button>
            </div>
          ) : (
            <div className="pt-4">
              <a
                href={lead.portal_user_id ? '/crm' : '/login'}
                className="inline-flex justify-center items-center py-3.5 px-6 rounded-2xl bg-slate-900 hover:bg-slate-850 text-white font-bold text-sm transition-all w-full text-center"
              >
                {lead.portal_user_id ? 'Go to My Portal' : 'Login / Explore ClickCollab'}
              </a>
            </div>
          )}

        </motion.div>
      </div>
    </div>
  );
};

export default CRMLeadRespondPage;
